import React, { useState } from 'react'

import { Button } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { reverse } from 'named-urls'

import { BaseConfirmationDialog, LoadingFeedback, MenuButton } from 'components'
import { ReactivateDialog } from './components'

import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'

import constants from 'constants/index'
import permissions from 'constants/permissions'
import { routes } from 'Routes'
import * as service from 'service'
import helpers from 'helpers'

const Actions = ({
  companySupplier = {},
  refresh = () => {},
  onRefreshStats = () => {},
  preAvaliation = false,
}) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openInactivateDialog, setOpenInactivateDialog] = useState(false)
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()
  const companySupplierConstants = constants.companySuppliers

  const companySupplierStatus = companySupplier?.status
  const isCompanySupplierActive =
    companySupplierStatus === companySupplierConstants.ACTIVE_STATUS
  const isCompanySupplierInactive =
    companySupplierStatus === companySupplierConstants.INACTIVE_STATUS

  const hasCompanySuppliersUpdatePermission = permitted(
    permissions.COMPANY_SUPPLIERS.UPDATE,
  )

  const handleEdit = () =>
    history.push({
      pathname: reverse(routes.thirdPartyManagements.mySuppliers.edit, {
        companySupplierId: companySupplier?.id,
      }),
      search: preAvaliation ? '?preAvaliation=true' : '',
    })

  const handleVisualize = () =>
    history.push({
      pathname: reverse(routes.thirdPartyManagements.mySuppliers.show, {
        companySupplierId: companySupplier?.id,
      }),
      search: preAvaliation ? '?preAvaliation=true' : '',
    })

  const handleOpenInactivateDialog = () => setOpenInactivateDialog(true)

  const handleInactivate = async () => {
    try {
      setLoading(true)

      await service.dponet.suppliers.inactivate({
        companySupplierId: companySupplier?.id,
      })

      openSuccessSnackbar('Fornecedor inativado com sucesso')

      setOpenInactivateDialog(false)
      refresh()
      onRefreshStats()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao inativar o fornecedor')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDeleteDialog = () => setOpenDeleteDialog(true)

  const handleDelete = async () => {
    try {
      setLoading(true)

      await service.dponet.suppliers.softDelete({
        companySupplierId: companySupplier?.id,
      })

      openSuccessSnackbar('Fornecedor excluído com sucesso')

      setOpenDeleteDialog(false)
      refresh()
      onRefreshStats()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao excluir o fornecedor')
    } finally {
      setLoading(false)
    }
  }

  const handleUpgradePreAvaliation = async () => {
    try {
      setLoading(true)

      await service.dponet.suppliers.upgradePreAvaliation({
        companySupplierId: companySupplier?.id,
      })

      openSuccessSnackbar('Pré-avaliação promovido para fornecedor com sucesso')
      refresh()
      onRefreshStats()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao atualizar')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenReactivateDialog = () => setOpenReactivateDialog(true)

  const handleReactivateSuccess = () => {
    refresh()
    onRefreshStats()
  }

  const handleResendEmail = async () => {
    try {
      setLoading(true)

      await service.dponet.suppliers.resendEmail({
        companySupplierId: companySupplier?.id,
      })

      openSuccessSnackbar('O e-mail será enviado em instantes')
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao reenviar o e-mail')
    } finally {
      setLoading(false)
    }
  }

  const openSuccessSnackbar = (message) =>
    snackbar.open({
      message,
      variant: 'success',
    })

  const openErrorSnackbar = (error, message) =>
    snackbar.open({
      message:
        helpers.formatters.errorMessage(error.response.data.error) || message,
      variant: 'error',
    })

  return (
    <>
      <LoadingFeedback open={loading} />
      <MenuButton>
        {isCompanySupplierActive && (
          <>
            <Button color="secondary" fullWidth onClick={handleVisualize}>
              Visualizar
            </Button>
            {hasCompanySuppliersUpdatePermission && (
              <>
                <Button color="secondary" fullWidth onClick={handleResendEmail}>
                  Reenviar e-mail
                </Button>
                <Button
                  color="secondary"
                  fullWidth
                  onClick={handleEdit}
                  disabled={isEmpty(companySupplier)}
                >
                  Editar
                </Button>
                {preAvaliation && (
                  <Button
                    color="secondary"
                    fullWidth
                    onClick={handleUpgradePreAvaliation}
                  >
                    Promover a Fornecedor
                  </Button>
                )}
                <Button
                  color="secondary"
                  fullWidth
                  onClick={handleOpenInactivateDialog}
                >
                  Inativar
                </Button>
              </>
            )}
          </>
        )}

        {hasCompanySuppliersUpdatePermission && (
          <>
            {isCompanySupplierInactive && (
              <Button
                color="secondary"
                fullWidth
                onClick={handleOpenReactivateDialog}
              >
                Reativar
              </Button>
            )}
            <Button
              color="secondary"
              fullWidth
              onClick={handleOpenDeleteDialog}
            >
              Excluir
            </Button>
          </>
        )}

        {!hasCompanySuppliersUpdatePermission && isCompanySupplierInactive && (
          <Button color="secondary" fullWidth disabled>
            NENHUMA AÇÃO DISPONÍVEL
          </Button>
        )}
      </MenuButton>

      {openDeleteDialog && (
        <BaseConfirmationDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          title="Tem certeza de que deseja excluir este fornecedor?"
          text={
            'Caso o contrato com o fornecedor tenha sido encerrado, sugerimos apenas inativá-lo na plataforma. Assim, o histórico de que o fornecedor foi avaliado será mantido para auditoria futura.\nDeseja confirmar a exclusão do fornecedor?'
          }
          actionButtonText="Confirmar"
          variant="warning"
          actionButton={handleDelete}
          loading={loading}
        />
      )}

      {openInactivateDialog && (
        <BaseConfirmationDialog
          open={openInactivateDialog}
          setOpen={setOpenInactivateDialog}
          title="Inativar fornecedor?"
          text="Deseja confirmar a inativação do fornecedor?"
          actionButtonText="Confirmar"
          variant="warning"
          actionButton={handleInactivate}
          loading={loading}
        />
      )}

      {openReactivateDialog && (
        <ReactivateDialog
          open={openReactivateDialog}
          setOpen={setOpenReactivateDialog}
          companySupplier={companySupplier}
          onSuccess={handleReactivateSuccess}
        />
      )}
    </>
  )
}

export default Actions
